import { CSV_FILENAME_PREFIX } from "./scorecardCopy";

/**
 * Flattens a built scorecard into rows for a CSV.
 *
 * Two decisions worth knowing:
 *
 * Each parameter becomes THREE columns — value, band, trend — rather than one
 * string like "1100 (Within benchmark, Stable)". Excel can sort and filter
 * three columns; it can do nothing useful with the string.
 *
 * Only the columns currently on screen are exported. The table is a view of a
 * sixteen-parameter configuration, and a file that silently contained more than
 * the page showed would be a quiet way to mislead someone.
 */
export function buildScorecardCsv({
  table,
  period,
  flowLabel,
  statisticLabel,
  scorecardLabel = "Watershed Plan Scorecard",
  generatedAt = new Date(),
}) {
  const { columns, rows } = table;
  const out = [];

  const window = (start, end) =>
    start || end ? `${start ?? "?"} to ${end ?? "?"}` : "not recorded";

  // A header block, because a bare grid of numbers is unreadable a week later
  // and the default period is Live, whose window moves.
  out.push([scorecardLabel]);
  out.push(["Period", period?.period_label ?? "unknown"]);
  out.push([
    "Statistics window",
    window(period?.stats_start, period?.stats_end),
  ]);
  out.push(["Trend window", window(period?.trend_start, period?.trend_end)]);
  out.push(["Flow condition", flowLabel]);
  out.push(["Statistic", statisticLabel]);
  out.push([
    "Exported",
    generatedAt.toISOString().slice(0, 19).replace("T", " "),
  ]);
  out.push([
    "Note",
    "Shading compares each value to CCBWQA benchmarks and is not an " +
      "assessment of attainment with Colorado water quality standards.",
  ]);
  out.push([]);

  const header = ["Reach", "Site", "Site description"];
  columns.forEach((column) => {
    const units =
      column.units && column.units !== "None" ? ` (${column.units})` : "";
    const scale = column.scaleNote ? ` [${column.scaleNote}]` : "";
    if (column.isStandard) {
      // A regulatory column: the seasonal mean, the attainment verdict with
      // the season tally, and the trend. Named so it cannot be confused with
      // the same parameter's water-year column.
      header.push(`${column.abbrev} summer mean${units}`);
      header.push(`${column.abbrev} standard`);
      header.push(`${column.abbrev} summer trend`);
      return;
    }
    header.push(`${column.abbrev}${units}${scale}`);
    header.push(`${column.abbrev} band`);
    header.push(`${column.abbrev} trend`);
  });
  out.push(header);

  rows.forEach((row) => {
    const line = [row.reachLabel, row.siteName, row.siteDescription ?? ""];
    columns.forEach((column) => {
      const cell = row.cells[column.key ?? column.parameterid];
      if (!cell || !cell.hasData) {
        // Empty rather than "n/a": a blank cell is what a spreadsheet expects
        // for a missing measurement, and it will not be mistaken for a value.
        line.push("", "", "");
        return;
      }
      line.push(cell.display ?? "");
      line.push(
        column.isStandard
          ? `${cell.bandLabel} (${cell.seasonsMet} of ${cell.seasonsTotal} seasons met; ${cell.bandRange})`
          : cell.bandRange
          ? `${cell.bandLabel} (${cell.bandRange})`
          : cell.bandLabel
      );
      line.push(
        cell.isSmallSample
          ? `${cell.trend.label} (n=${cell.recordcount}, small sample)`
          : cell.trend.label
      );
    });
    out.push(line);
  });

  return out;
}

/** wq-scorecard-last5-alldata-median-2026-09-17.csv */
export function buildScorecardCsvFilename({
  periodKey = "period",
  flow = "alldata",
  statistic = "median",
  generatedAt = new Date(),
}) {
  const date = generatedAt.toISOString().slice(0, 10);
  return `${CSV_FILENAME_PREFIX}-${periodKey}-${flow}-${statistic}-${date}.csv`;
}

export default buildScorecardCsv;
